/**
 * Modal s fotogalerií jedné akce — zobrazení všech fotek + přidávání nových.
 * Otevři: openEventPhotos(eventId, label)
 */
import { supabase } from './supabase.js';
import { openLightbox } from './lightbox.js';
import { getMediaType, mediaThumbHTML, ACCEPT_ALL_MEDIA } from './media.js';
import { uploadEventPhoto, uploadFiles } from './photos.js';
import { willCompressVideo } from './video-compress.js';

const BUCKET = 'photos-event';

function ensureModal() {
    let m = document.getElementById('event-photos-modal');
    if (m) return m;
    m = document.createElement('div');
    m.id = 'event-photos-modal';
    m.className = 'place-photos-modal';
    m.innerHTML = `
        <div class="pp-window">
            <div class="pp-head">
                <h2 id="ep-title">Fotogalerie akce</h2>
                <button class="modal-close" type="button" id="ep-close">×</button>
            </div>
            <div id="ep-body" class="pp-grid"></div>
        </div>
    `;
    document.body.appendChild(m);
    const close = () => m.classList.remove('open');
    const lightboxOpen = () => document.getElementById('lightbox')?.classList.contains('open');
    m.querySelector('#ep-close').addEventListener('click', close);
    m.addEventListener('click', (e) => { if (e.target === m && !lightboxOpen()) close(); });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && m.classList.contains('open') && !lightboxOpen()) close();
    });
    return m;
}

export async function openEventPhotos(eventId, label) {
    const modal = ensureModal();
    modal.querySelector('#ep-title').textContent = label ? `Fotogalerie · ${label}` : 'Fotogalerie akce';
    const body = modal.querySelector('#ep-body');
    body.innerHTML = '<div class="pp-empty">Načítám…</div>';
    modal.classList.add('open');

    const { data, error } = await supabase
        .from('photos')
        .select('*')
        .eq('type', 'event')
        .eq('event_id', eventId)
        .order('uploaded_at', { ascending: false });

    if (error) {
        body.innerHTML = `<div class="pp-empty" style="color:var(--accent);">Chyba: ${error.message}</div>`;
        return;
    }

    const list = data || [];
    const urls = await Promise.all(list.map(async (p) => {
        const { data: u } = await supabase.storage.from(BUCKET).createSignedUrl(p.storage_path, 3600);
        return u?.signedUrl || null;
    }));

    body.innerHTML = '';

    // Dlaždice pro přidání nových fotek
    const addTile = document.createElement('label');
    addTile.className = 'ep-add';
    addTile.title = 'Přidat fotky z akce';
    addTile.innerHTML = `
        <span class="ep-add-plus">+</span>
        <span class="ep-add-txt">Přidat foto</span>
        <input type="file" accept="${ACCEPT_ALL_MEDIA}" multiple style="display:none;">
    `;
    const fileInput = addTile.querySelector('input');
    fileInput.addEventListener('change', async () => {
        if (!fileInput.files?.length) return;
        addTile.classList.add('busy');
        const txt = addTile.querySelector('.ep-add-txt');
        const { fail, errors } = await uploadFiles(
            uploadEventPhoto, eventId, fileInput.files,
            ({ ok, total }) => { if (txt) txt.textContent = `Nahráno ${ok}/${total}…`; },
            (file) => { if (willCompressVideo(file) && txt) txt.textContent = 'Komprimuji video…'; }
        );
        if (fail) {
            alert('Některé fotky se nepodařilo nahrát:\n' +
                errors.map((e) => `• ${e.name}: ${e.message}`).join('\n'));
        }
        openEventPhotos(eventId, label); // refresh
    });
    body.appendChild(addTile);

    if (list.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'pp-empty';
        empty.textContent = 'Zatím žádné fotky z této akce. Přidej první přes dlaždici „+“.';
        body.appendChild(empty);
        return;
    }

    const photoArr = list
        .map((p, idx) => urls[idx] ? ({
            url: urls[idx],
            filename: p.storage_path.split('/').pop(),
            bucket: BUCKET,
            path: p.storage_path,
            photoId: p.id
        }) : null)
        .filter(Boolean);

    photoArr.forEach((photo, idx) => {
        const type = getMediaType(photo.path);
        const wrap = document.createElement('div');
        wrap.style.cssText = 'cursor:pointer;position:relative;';
        wrap.innerHTML = mediaThumbHTML({ url: photo.url, path: photo.path, mediaType: type });
        const inner = wrap.firstElementChild;
        if (inner) {
            inner.style.width = '200px';
            inner.style.height = '200px';
            inner.style.borderRadius = '6px';
        }
        wrap.addEventListener('click', () => {
            openLightbox({
                photos: photoArr, startIndex: idx,
                onDelete: () => openEventPhotos(eventId, label)
            });
        });
        body.appendChild(wrap);
    });
}
